import { shallowEqual, useSelector } from "react-redux";
import { Navigate } from "react-router";
import { ColoredLabel, formatDateTimeFromISO } from "widgets";

import { getEventTagIntent, getTagIntentColor } from "@/app-utils/events.js";
import { getIconElementForPlaybook } from "@/app-utils/playbook.js";
import { getPlaybookEventName, type IEventState } from "@/redux/modules/event.js";
import type { IAppState } from "@/redux/reducer.js";
import GenericView from "@/ui-modules/entities/GenericView.tsx";
import { PlaybookEventWorkflowPreviewCard } from "@/ui-modules/entities/PlaybookEventWorkflowPreviewCard.tsx";

import { AttributesListWidget } from "../core/navigation-panel/AttributeWidget.js";

const EventWorkflowView = () => {
  const { event, isLoading } = useSelector<IAppState, IEventState>((state) => state.event, shallowEqual);
  // if this is a job-related event, proceed to the job summary page right away
  if (event?.job) {
    return <Navigate to={`/job/${event.job?.id}`} replace />;
  }

  if (!event) {
    // should never happen as we will get an error page.
    // just to make typescript happy
    return null;
  }

  return (
    <GenericView
      title={getPlaybookEventName(event)}
      subtitle={"Preview"}
      entity={event}
      project={event?.project}
      headerTags={[
        <img
          src={getIconElementForPlaybook(event.playbook).large}
          width={53}
          height={30}
          key={"event-summary-view-tag-3"}
          alt={"playbook icon"}
        />,
      ]}
      headerAttributes={[
        <AttributesListWidget
          attributes={[
            { label: "Date", description: formatDateTimeFromISO(event.created) },
            { label: "Playbook", description: event.playbook.name },
            {
              label: "Status",
              description: (
                <ColoredLabel color={getTagIntentColor(getEventTagIntent(event.status))} label={event.status} />
              ),
            },
          ]}
          key={"event-summary-view-widget-2"}
        />,
      ]}
      isLoading={isLoading}
    >
      {event ? (
        <PlaybookEventWorkflowPreviewCard
          workflowP2ExtendedConfig={event.workflowP2ExtendedConfig}
          eventId={event.id}
        />
      ) : null}
    </GenericView>
  );
};

export default EventWorkflowView;
